import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppRoutes, SCREEN_SIZE_BREAKPOINTS, SIDEBAR_WIDTH } from '../../constants';
import { useWindowResize } from '../../hooks';
import { TextColor } from '../../types';
import { Icon, Icons } from '../shared';
import AnalyticsSidebarContent from './AnalyticsSidebarContent/AnalyticsSidebarContent';
import KnowledgeHubSidebarContent from './KnowledgeHubSidebarContent';
import ReviewSidebarContent from './ReviewSidebarContent/ReviewSidebarContent';
import ScoringAndCoachingSidebarContent from './ScoringAndCoachingSidebarContent';
import SettingsSidebarContent from './SettingsSidebarContent';
import SidebarContent from './SidebarContent';
import SimulationsSidebarContent from './SimulationsSidebarContent/SimulationsSidebarContent';

const SIDEBAR_BORDER_WIDTH = 1;
const SIDEBAR_SHADOW_BLUR_RADIUS = 5;

const Sidebar = () => {
  const { pathname } = useLocation();

  const [isCollapsed, setIsCollapsed] = useState(false);

  const windowWidth = useWindowResize();

  // Automatically collapse the sidebar for window widths less than or equal to the threshold.
  useEffect(() => {
    setIsCollapsed((prev) => {
      if (windowWidth > SCREEN_SIZE_BREAKPOINTS.TABLET && prev) return false;
      if (windowWidth <= SCREEN_SIZE_BREAKPOINTS.TABLET && !prev) return true;
      return prev;
    });
  }, [windowWidth]);

  const getSidebarData = () => {
    // Create routes should not have a sidebar.
    if (pathname.endsWith('create')) {
      return null;
    }

    if (pathname.includes(AppRoutes.SIMULATIONS)) {
      return { title: 'Simulations', content: <SimulationsSidebarContent /> };
    }

    if (pathname.includes(AppRoutes.REVIEW)) {
      return { title: 'Review', content: <ReviewSidebarContent /> };
    }

    if (pathname.includes(AppRoutes.ANALYTICS)) {
      return { title: 'Analytics', content: <AnalyticsSidebarContent /> };
    }

    if (pathname.includes(AppRoutes.KNOWLEDGE_HUB)) {
      return { title: 'Knowledge hub', content: <KnowledgeHubSidebarContent />, showBackButton: true };
    }

    if (pathname.includes(AppRoutes.SETTINGS)) {
      return { title: 'Settings', content: <SettingsSidebarContent />, showBackButton: true };
    }

    if (pathname === AppRoutes.SCORECARDS || pathname === AppRoutes.COACHING_RUBRICS) {
      return {
        title: 'Scoring & coaching',
        content: <ScoringAndCoachingSidebarContent />,
        showBackButton: true,
      };
    }
  };

  const sidebarData = getSidebarData();

  // If no sidebar data exist for the current page, don't render the sidebar.
  if (!sidebarData) return null;

  return (
    <div
      className={clsx(
        'border-base-300 relative transform items-start border-r bg-base-150 py-6 transition-[width,padding] duration-300',
        !isCollapsed && 'px-4'
      )}
      style={{
        boxShadow: `0px 0px ${SIDEBAR_SHADOW_BLUR_RADIUS}px 0px rgba(91,94,93,0.20) inset`,
        width: isCollapsed ? 0 : SIDEBAR_WIDTH,
      }}
    >
      {/* Sidebar content */}
      {!isCollapsed && <SidebarContent {...sidebarData} />}
      {/* Collapse button */}
      <div
        className="absolute top-4 flex h-7 cursor-pointer rounded-r-[4px] transition-[left] duration-300"
        style={{
          left: isCollapsed ? 0 : SIDEBAR_WIDTH - SIDEBAR_SHADOW_BLUR_RADIUS - SIDEBAR_BORDER_WIDTH,
        }}
        onClick={() => setIsCollapsed((prev) => !prev)}
      >
        {!isCollapsed && <div className="h-full bg-base-150" style={{ width: SIDEBAR_SHADOW_BLUR_RADIUS }} />}
        <div className="border-base-300 flex h-full items-center justify-center rounded-r-[4px] border-y bg-base-150 shadow-[-1px_0px_1px_0px_rgba(0,0,0,0.10)_inset]">
          <Icons icon={isCollapsed ? Icon.CHEVRON_RIGHT : Icon.CHEVRON_LEFT} color={TextColor.SECONDARY} />
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
